import React from 'react'
import { Payout } from '../../types'
import { format, parse } from 'date-fns'
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import Spinner from '../Spinner'
import Tooltip from '../Tooltip'
import { MONETIZATION_GLOSSARY } from '../../config/glossary'
import { FeeRates, feeTerms } from './FeeEstimate'

interface PayoutHistoryProps {
  payouts: Payout[]
  onRequestPayout?: () => void
  isLoading?: boolean
  isRequesting?: boolean
  /** The rates payments are recorded with; null: "fees shown at payout". */
  feeRates?: FeeRates | null
}

const PayoutHistory: React.FC<PayoutHistoryProps> = ({
  payouts,
  onRequestPayout,
  isLoading = false,
  isRequesting = false,
  feeRates = null,
}) => {
  const getStatusBadge = (status: Payout['status']) => {
    switch (status) {
      case 'PAID':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Paid
          </span>
        )
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="h-4 w-4 mr-1" />
            Pending
          </span>
        )
      case 'PROCESSING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Processing
          </span>
        )
      case 'FAILED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="h-4 w-4 mr-1" />
            Failed
          </span>
        )
      default:
        return null
    }
  }

  const formatPeriod = (period: string) => {
    try {
      return format(parse(period, 'yyyy-MM', new Date()), 'MMMM yyyy')
    } catch {
      return period
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              Payout History
              <Tooltip content={MONETIZATION_GLOSSARY.payouts} label="a payout" />
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Track payouts of your available balance via Stripe Connect
            </p>
            <p className="text-xs text-gray-500 mt-2" data-testid="payout-fees">
              {feeRates
                ? `You receive each payment minus fees: ${feeTerms(feeRates)}.${
                    feeRates.payout_minimum_cents
                      ? ` Minimum payout $${(feeRates.payout_minimum_cents / 100).toFixed(2)}.`
                      : ''
                  }`
                : 'Fees shown at payout.'}
            </p>
          </div>
          {onRequestPayout && (
            <button
              onClick={onRequestPayout}
              disabled={isRequesting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRequesting ? 'Requesting…' : 'Request Payout'}
            </button>
          )}
        </div>
      </div>

      {payouts.length === 0 ? (
        <div className="p-12 text-center">
          <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No payout history</p>
          <p className="text-sm text-gray-500 mt-2">
            Your payout requests will appear here once you request one
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payout Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payouts.map((payout) => (
                <tr key={payout.period} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatPeriod(payout.period)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">
                      ${payout.amount.toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(payout.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {payout.payout_date
                      ? format(new Date(payout.payout_date), 'MMM d, yyyy')
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {payout.reference || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {payouts.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Total payouts: {payouts.length}</p>
            <p className="text-sm font-medium text-gray-900">
              Total paid: $
              {payouts
                .filter((p) => p.status === 'PAID')
                .reduce((sum, p) => sum + p.amount, 0)
                .toFixed(2)}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default PayoutHistory
